import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Plus, Trash2, Loader2, Users, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

const NEAR_LIMIT_PCT = 80;

function quotaRowLevel(used: number, contracted: number): 'ok' | 'warning' | 'critical' {
  const c = contracted || 0;
  if (c <= 0) return 'ok';
  if (used >= c) return 'critical';
  const pct = (used / c) * 100;
  if (pct >= NEAR_LIMIT_PCT) return 'warning';
  return 'ok';
}

interface Props {
  org: { id: string; name: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function OrgQuotasDialog({ org, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const orgId = org?.id;

  const { data: programs = [] } = useQuery({
    queryKey: ['programs-list', 'guided'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select('id, name, modality')
        .eq('modality', 'cohort')
        .order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  // Nombres de rutas de cupos ya guardados que no están en la lista filtrada
  // (por ejemplo cupos antiguos sobre rutas de autoaprendizaje).
  const { data: extraProgramNames = {} } = useQuery({
    queryKey: ['org-quota-extra-programs', orgId],
    enabled: !!orgId && open,
    queryFn: async () => {
      const { data, error } = await supabase.from('programs').select('id, name');
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((p: any) => { map[p.id] = p.name; });
      return map;
    },
  });


  const { data: quotas = [], isLoading } = useQuery({
    queryKey: ['org-quotas', orgId],
    enabled: !!orgId && open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organization_program_quotas')
        .select('*')
        .eq('organization_id', orgId!);
      if (error) throw error;
      return data;
    },
  });

  // Count used slots: participants in cohorts of this org per program
  const { data: usedMap = {} } = useQuery({
    queryKey: ['org-quota-usage', orgId],
    enabled: !!orgId && open,
    queryFn: async () => {
      // Get cohorts for this org
      const { data: cohorts, error: cErr } = await supabase
        .from('cohorts')
        .select('id, program_id')
        .eq('organization_id', orgId!);
      if (cErr) throw cErr;
      if (!cohorts?.length) return {} as Record<string, number>;

      const cohortIds = cohorts.map(c => c.id);
      const { data: participants, error: pErr } = await supabase
        .from('cohort_participants')
        .select('cohort_id')
        .in('cohort_id', cohortIds);
      if (pErr) throw pErr;

      const counts: Record<string, number> = {};
      (participants || []).forEach(p => {
        const cohort = cohorts.find(c => c.id === p.cohort_id);
        if (cohort?.program_id) {
          counts[cohort.program_id] = (counts[cohort.program_id] || 0) + 1;
        }
      });
      return counts;
    },
  });

  const upsertMut = useMutation({
    mutationFn: async (input: { organization_id: string; program_id: string; contracted_qty: number }) => {
      const { error } = await supabase.from('organization_program_quotas').upsert(input, {
        onConflict: 'organization_id,program_id',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-quotas', orgId] });
      qc.invalidateQueries({ queryKey: ['org-quota-health'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('organization_program_quotas').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-quotas', orgId] });
      qc.invalidateQueries({ queryKey: ['org-quota-health'] });
    },
  });

  const [addingProgram, setAddingProgram] = useState('');
  const [addingQty, setAddingQty] = useState('');

  const assignedProgramIds = quotas.map((q: any) => q.program_id);
  const availablePrograms = programs.filter(p => !assignedProgramIds.includes(p.id));

  const handleAdd = async () => {
    if (!addingProgram || !addingQty || !orgId) return;
    try {
      await upsertMut.mutateAsync({
        organization_id: orgId,
        program_id: addingProgram,
        contracted_qty: parseInt(addingQty),
      });
      setAddingProgram('');
      setAddingQty('');
      toast({ title: 'Cupo agregado' });
    } catch {
      toast({ title: 'Error al agregar cupo', variant: 'destructive' });
    }
  };

  const handleUpdate = async (quota: any, newQty: number) => {
    try {
      await upsertMut.mutateAsync({
        organization_id: quota.organization_id,
        program_id: quota.program_id,
        contracted_qty: newQty,
      });
      toast({ title: 'Cupo actualizado' });
    } catch {
      toast({ title: 'Error al actualizar', variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMut.mutateAsync(id);
      toast({ title: 'Cupo eliminado' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const getProgramName = (pid: string) =>
    programs.find(p => p.id === pid)?.name || (extraProgramNames as Record<string, string>)[pid] || pid;

  const quotaLevels = (quotas as { id: string; program_id: string; contracted_qty: number }[]).map((q) => {
    const used = usedMap[q.program_id] || 0;
    return quotaRowLevel(used, q.contracted_qty);
  });
  const hasCritical = quotaLevels.some((l) => l === 'critical');
  const hasWarning = quotaLevels.some((l) => l === 'warning');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cupos por Ruta — {org?.name}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            {quotas.length > 0 && (hasCritical || hasWarning) && (
              <Alert
                className={cn(
                  hasCritical
                    ? 'border-destructive/50 bg-destructive/5 [&>svg]:text-destructive'
                    : 'border-warning/50 bg-warning/5 [&>svg]:text-warning',
                )}
              >
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {hasCritical
                    ? 'Hay rutas con cupos agotados o uso por encima del contratado. Revisa la tabla o aumenta los cupos.'
                    : `Hay rutas con consumo de cupos ≥ ${NEAR_LIMIT_PCT}% del contratado.`}
                </AlertDescription>
              </Alert>
            )}
            {quotas.length > 0 && (
              <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ruta</TableHead>
                      <TableHead className="text-center">Contratados</TableHead>
                      <TableHead className="text-center">Usados</TableHead>
                      <TableHead className="text-center">Disponibles</TableHead>
                      <TableHead className="w-16" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotas.map((q: any) => {
                      const used = usedMap[q.program_id] || 0;
                      const available = q.contracted_qty - used;
                      const rowLevel = quotaRowLevel(used, q.contracted_qty);
                      const pct = q.contracted_qty > 0 ? (used / q.contracted_qty) * 100 : 0;
                      const colorClass =
                        rowLevel === 'critical'
                          ? 'text-destructive font-semibold'
                          : rowLevel === 'warning'
                            ? 'text-warning font-medium'
                            : 'text-foreground';
                      return (
                        <TableRow
                          key={q.id}
                          className={cn(
                            rowLevel === 'critical' && 'bg-destructive/5',
                            rowLevel === 'warning' && 'bg-warning/5',
                          )}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {rowLevel !== 'ok' && (
                                <AlertTriangle
                                  className={cn(
                                    'h-4 w-4 shrink-0',
                                    rowLevel === 'critical' ? 'text-destructive' : 'text-warning',
                                  )}
                                  aria-hidden
                                />
                              )}
                              {getProgramName(q.program_id)}
                            </div>
                            {q.contracted_qty > 0 ? (
                              <div className="mt-1 h-1 w-full max-w-[180px] overflow-hidden rounded-full bg-muted">
                                <div
                                  className={cn(
                                    'h-full rounded-full transition-all',
                                    rowLevel === 'critical'
                                      ? 'bg-destructive'
                                      : rowLevel === 'warning'
                                        ? 'bg-warning'
                                        : 'bg-success',
                                  )}
                                  style={{ width: `${Math.min(pct, 100)}%` }}
                                />
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              className="w-20 mx-auto text-center"
                              value={q.contracted_qty}
                              min={0}
                              onChange={e => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val) && val >= 0) handleUpdate(q, val);
                              }}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className={`flex items-center justify-center gap-1 ${colorClass}`}>
                              <Users className="w-3.5 h-3.5" />
                              <span>{used}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={
                                rowLevel === 'critical'
                                  ? 'destructive'
                                  : rowLevel === 'warning'
                                    ? 'warning'
                                    : 'success'
                              }
                            >
                              {available}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {quotas.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No hay cupos configurados para esta organización.</p>
            )}

            {/* Add new quota */}
            {availablePrograms.length > 0 && (
              <div className="flex items-end gap-3 pt-2 border-t border-border">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Ruta</label>
                  <Select value={addingProgram} onValueChange={setAddingProgram}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar ruta" /></SelectTrigger>
                    <SelectContent>
                      {availablePrograms.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24">
                  <label className="text-xs text-muted-foreground mb-1 block">Cupos</label>
                  <Input type="number" min={1} value={addingQty} onChange={e => setAddingQty(e.target.value)} placeholder="Ej: 20" />
                </div>
                <Button size="sm" onClick={handleAdd} disabled={!addingProgram || !addingQty}>
                  <Plus className="w-4 h-4 mr-1" />Agregar
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
